import { useEffect, useRef, useState } from "react";
import { perfMark } from "../perf";
import {
  Button,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginatedTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  fetchPage: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  enableSearch?: boolean;
  onRowClick?: (row: T) => void;
  /** Message shown when the result set is empty. Defaults to "No results". */
  emptyMessage?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 30, 50, 100];

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: tokens.spacingVerticalS,
  },
  table: {
    width: "100%",
  },
  clickableRow: {
    cursor: "pointer",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalS,
    flexWrap: "wrap",
    rowGap: tokens.spacingVerticalXS,
  },
  countText: {
    color: tokens.colorNeutralForeground3,
  },
  pageSizeSelector: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
  },
  pageButtons: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalXXS,
  },
  pageLabel: {
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
  },
  loadingRow: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalS,
  },
});

export function PaginatedTable<T>({
  columns,
  fetchPage,
  defaultPageSize = 25,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onRowClick,
  emptyMessage,
}: PaginatedTableProps<T>): React.JSX.Element {
  const styles = useStyles();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [response, setResponse] = useState<PaginatedResponse<T> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Keep fetchPage in a ref so the data-fetch effect doesn't depend on it.
  // A change to fetchPage resets the page to 1 via fetchToken, which
  // triggers a refetch through the [fetchToken, page, pageSize] deps.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  // Incremented whenever fetchPage identity changes to signal a refetch.
  const [fetchToken, setFetchToken] = useState(0);
  const prevFetchPageRef = useRef(fetchPage);

  if (prevFetchPageRef.current !== fetchPage) {
    prevFetchPageRef.current = fetchPage;
    // New fetchPage → reset page and bump the token so the effect re-runs.
    setPage(1);
    setFetchToken((t) => t + 1);
  }

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchPageRef.current(page, pageSize)
      .then((result) => {
        if (!cancelled) {
          setResponse(result);
          setIsLoading(false);
          perfMark("PaginatedTable:data-rendered", {
            total: result.totalCount,
            page: result.page,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // fetchToken changes when the fetchPage function reference changes,
    // ensuring a refetch without depending on the function identity directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchToken, page, pageSize]);

  const table = useReactTable({
    data: response?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalCount = response?.totalCount ?? 0;
  const totalPages = response?.totalPages ?? 0;
  const effectiveTotalPages = Math.max(totalPages, 1);

  const handlePageSizeChange = (newSize: number): void => {
    setPageSize(newSize);
    setPage(1);
  };

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.loadingRow}>
          <Spinner size="tiny" label="Loading..." />
        </div>
      ) : null}

      <Table size="small" className={styles.table}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell key={header.id} data-column-id={header.column.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                style={{ textAlign: "center", padding: "2rem" }}
              >
                <Text size={200}>{emptyMessage ?? "No results"}</Text>
              </TableCell>
            </TableRow>
          ) : null}
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={onRowClick ? styles.clickableRow : undefined}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} data-column-id={cell.column.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className={styles.controls}>
        {/* Rows per page — left side */}
        <div className={styles.pageSizeSelector}>
          <Text size={200}>Rows per page</Text>
          <Select
            size="small"
            value={String(pageSize)}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Select>
        </div>

        {/* Pagination — right side: mirrors reference “< Prev | Page X of Y (N total) | Next >” */}
        <div className={styles.pageButtons}>
          <Button
            appearance="subtle"
            size="small"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            aria-label="Previous page"
          >
            {"< Prev"}
          </Button>
          <Text size={200} className={styles.pageLabel}>
            Page {page} of {effectiveTotalPages}
            {totalCount > 0 ? ` (${totalCount.toLocaleString()} total)` : ""}
          </Text>
          <Button
            appearance="subtle"
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            {"Next >"}
          </Button>
        </div>
      </div>
    </div>
  );
}
